import { zodResolver } from '@hookform/resolvers/zod'
import CustomInput from 'components/CustomInput'
import { isAnonymousBrand } from '@cannect/config/brands.config'
import { useBrand } from 'hooks/useBrand'
import { useScheduling } from 'hooks/useScheduling'
import { setUser } from 'hooks/useSessionUser'
import { useTokenApi } from 'hooks/useTokenApi'
import { Loader2 } from 'lucide-react'
import Button from 'new-components/Button'
import { SimplifiedIdentificationFormSchema } from 'pages/AppointmentScheduling/schedulingMock'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { bindScreeningCpf, getScreeningNext } from '@cannect/services/resources/screening/requests'
import { SCREENING_TOKEN_KEY } from '@cannect/services/resources/screening/requests'
import { FormProvider, SubmitHandler, useForm, useFormContext } from 'react-hook-form'
import { toast } from 'react-toastify'
import api, { defaultsHeadersAxios } from 'services/api'
import { getSessionCartId, markSessionCartResolved } from 'utils/anonymousCart'
import { clearBrandedIdentification, markBrandedIdentification } from 'utils/brandedIdentification'
import { clearMask, dateMask, formatCpf, formatPhone } from 'utils/formatters'
import { NavigateButtonsWrapper } from '../../styles'

export interface IdentificationFormProps {
  cpf: string
  birthday: string
  email: string
  name: string
  whatsapp: string
}

interface SimplifiedRegisterSuccess {
  success: boolean
  // Whitelabel: token de sessão para autenticação transparente (sem tela de login)
  token?: string
  user: {
    uuid: string
    accepted_terms_of_use_at: any
    prescriber: boolean
    listed_prescriber: boolean
    integrated: boolean
    origin: string
    admission_date: any
    id: number
    name: string
    email: string
    phone: string
    registration_type: number
    cpf: string
    updatedAt: string
    createdAt: string
    refresh_token: string
  }
  message: string
}
export default function SimplifiedRegisterForm() {
  const { back, setLoading, nextCheckoutStep } = useScheduling()
  const { brand, config } = useBrand()
  const { setTokenApi, deleteTokenApi } = useTokenApi()
  // Marca alias (ex.: Vikura) registra com a origem real (remederi).
  const brandedOrigin = config.scheduling.origin ?? (brand !== 'cannect' ? brand : null)
  const { setValue } = useFormContext()
  const location = useLocation()
  const screeningToken =
    new URLSearchParams(location.search).get('screening') || sessionStorage.getItem(SCREENING_TOKEN_KEY)
  const [dateFormated, setDateFormated] = useState<string>()
  const [cpfFormatted, setCpfFormatted] = useState<string>()
  const [phoneFormatted, setPhoneFormatted] = useState<string>()
  const [submitting, setSubmitting] = useState(false)
  const simplifiedRegisterForm = useForm<IdentificationFormProps>({
    mode: 'onChange',
    shouldUnregister: false,
    resolver: zodResolver(SimplifiedIdentificationFormSchema),
    defaultValues: {
      cpf: '',
      birthday: '',
      email: '',
      name: '',
      whatsapp: ''
    }
  })

  const { handleSubmit, reset, getValues, setValue: setFormValue } = simplifiedRegisterForm

  useEffect(() => {
    if (!screeningToken) return
    getScreeningNext(screeningToken)
      .then((next) => {
        const contact = next.contact
        if (!contact) return
        if (contact.patient_name) setFormValue('name', contact.patient_name, { shouldValidate: true })
        const email = contact.contact_email || contact.responsible_email
        if (email) setFormValue('email', email, { shouldValidate: true })
        const phone = contact.contact_phone || contact.responsible_phone
        if (phone) {
          const masked = formatPhone(String(phone))
          setPhoneFormatted(masked)
          setFormValue('whatsapp', masked, { shouldValidate: true })
        }
        if (contact.birthday) {
          const [year, month, day] = contact.birthday.split('-')
          if (year && month && day) {
            const masked = `${day}/${month}/${year}`
            setDateFormated(masked)
            setFormValue('birthday', masked, { shouldValidate: true })
          }
        }
      })
      .catch(() => undefined)
  }, [screeningToken, setFormValue])

  const handleBack = () => {
    reset()
    back()
  }

  const handleRegister: SubmitHandler<IdentificationFormProps> = async () => {
    try {
      setLoading(true)
      setSubmitting(true)
      const { cpf, email, name, whatsapp } = getValues()

      const sessionCartId = getSessionCartId()
      const response = await api.post<SimplifiedRegisterSuccess>('/register-or-retrieve?generatePassword=true', {
        name,
        email,
        birthday: dateFormated,
        cpf: clearMask(cpf),
        nationality: 'brasileira',
        whatsapp: clearMask(whatsapp),
        registration_type: 1,
        ...(brandedOrigin ? { origin: brandedOrigin } : {}),
        ...(sessionCartId ? { session_cart_id: sessionCartId } : {})
      })
      if (sessionCartId) {
        markSessionCartResolved(sessionCartId)
      }

      const { user, token } = response.data

      // Whitelabel: autentica de forma transparente (sem tela de login) — a
      // sessão vale para toda a jornada, incluindo o pulo para a Consulta na Hora.
      if (token) {
        setTokenApi(token)
        defaultsHeadersAxios(token)
        await setUser(user)
        if (isAnonymousBrand(brand)) markBrandedIdentification(brand, user?.id ?? null)
      } else if (isAnonymousBrand(brand)) {
        // Sem token (ex.: dados não batem com o cadastro existente): descarta
        // qualquer sessão anterior do navegador — pode ser de outra pessoa e
        // seria usada na Consulta na Hora. O agendamento segue sem token.
        deleteTokenApi()
        delete api.defaults.headers.common.Authorization
        clearBrandedIdentification()
      }

      if (screeningToken) {
        try {
          await bindScreeningCpf(screeningToken, clearMask(cpf))
        } catch (bindError: any) {
          toast.error(
            bindError?.response?.data?.message || 'Não foi possível vincular sua avaliação ao CPF. Tente novamente.'
          )
          return
        }
      }

      // O branch "usuário já cadastrado" do register-or-retrieve devolve só
      // { id, uuid } (hardening de PII — rota anônima), mas a criação do
      // agendamento e o pagamento precisam do cpf: completa o patient com os
      // dados que o próprio formulário coletou.
      const formCpf = clearMask(cpf)

      await setValue('patient', {
        ...user,
        cpf: formCpf,
        name: user.name ?? name,
        email: user.email ?? email,
        phone: clearMask(whatsapp) || user.phone
      })

      nextCheckoutStep()
    } catch (err: any) {
      toast.error('Houve um erro ao realizar o cadastro. Tente novamente mais tarde.')
    } finally {
      setLoading(false)
      setSubmitting(false)
    }
  }

  return (
    <FormProvider {...simplifiedRegisterForm}>
      <form onSubmit={handleSubmit(handleRegister)}>
        <CustomInput
          outlined
          maxLength={14}
          type="cpf"
          name="cpf"
          label="CPF *"
          placeholder="CPF "
          value={cpfFormatted}
          onChange={(e) => {
            const masked = formatCpf(e.target.value)
            setCpfFormatted(masked)
            setFormValue('cpf', masked, { shouldValidate: true })
          }}
        />
        <CustomInput outlined name="name" label="Nome do paciente *" placeholder="Nome " />
        <CustomInput
          outlined
          type="date"
          onChange={(e) => {
            setDateFormated(dateMask(e.target.value))
          }}
          value={dateFormated}
          placeholder="dd/mm/aaaa"
          name="birthday"
          label="Data de nascimento *"
          maxLength={10}
        />
        <CustomInput outlined name="email" label="E-mail *" placeholder="E-mail" />
        <CustomInput
          outlined
          name="whatsapp"
          label="Telefone *"
          type="phone"
          placeholder="Telefone"
          maxLength={15}
          value={phoneFormatted}
          onChange={(e) => {
            // O field.onChange do CustomInput roda antes e guarda o valor cru;
            // regrava o mascarado no form para exibição e validação baterem.
            const masked = formatPhone(e.target.value)
            setPhoneFormatted(masked)
            setFormValue('whatsapp', masked, { shouldValidate: true })
          }}
        />
        <NavigateButtonsWrapper usage="identification">
          {!brand ||
            (brand === 'cannect' && (
              <Button type="button" isOutlined background="black" onClick={handleBack}>
                Voltar
              </Button>
            ))}
          <Button type="submit" background="green" disabled={submitting}>
            {submitting && <Loader2 className="mr-2 inline size-4 animate-spin" />}
            Salvar e continuar
          </Button>
        </NavigateButtonsWrapper>
      </form>
    </FormProvider>
  )
}
